import React, { useState, useMemo, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import CleanProjectNames from '/src/services/CleanProjectNames';
import CTAButton from '/src/Components/CTAButton/CTAButton';
import { MoveRight, CircleX } from 'lucide-react';
import Confetti from 'react-confetti';
import { WindowSizeContext } from '/src/App';

import './style.css';

const ContentWrapper = ({ children, inline = false, handleOutsideClick = null, confetti = false }) => {
    if (inline) {
        return children;
    } else {
        const popupRef = useRef(null);

        useEffect(() => {
            const handleClick = (event) => {
                if (popupRef.current && !popupRef.current.contains(event.target)) {
                    handleOutsideClick?.();
                }
            };

            if (handleOutsideClick) {
                document.addEventListener('mousedown', handleClick);
                return () => document.removeEventListener('mousedown', handleClick);
            }
        }, [handleOutsideClick]);

        // Add effect to handle body scroll
        useEffect(() => {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }, []);

        return createPortal(
            <div className="schedule-time-with-quoll-overlay">
                <div
                    className={`schedule-time-with-quoll-popup ${confetti ? 'confetti' : ''}`}
                    ref={popupRef}
                    onClick={e => e.stopPropagation()}
                >
                    {handleOutsideClick && (
                        <button
                            className="user-story-personalization-form-close-btn"
                            aria-label="Close personalization form"
                            onClick={handleOutsideClick}
                            type="button"
                        >
                            <CircleX />
                        </button>
                    )}
                    {children}
                </div>
            </div>,
            document.body
        );
    }
};

const SendProjectToOthers = ({ propertyData, projects, showTitle = true, inline = false, handleOutsideClick = null }) => {
    const { deviceType } = useContext(WindowSizeContext);
    const [processStage, setProcessStage] = useState(0);
    const [projectsToDiscuss, setProjectsToDiscuss] = useState([]);
    const [recipientEmails, setRecipientEmails] = useState('');
    const [personalNote, setPersonalNote] = useState('');
    const [showConfetti, setShowConfetti] = useState(false);
    const [windowDimension, setWindowDimension] = useState({ width: window.innerWidth, height: window.innerHeight });


    const handleConfetti = () => {

        setShowConfetti(true);
        setTimeout(() => {
            setShowConfetti(false);
            handleOutsideClick?.();
        }, 4000);
    }

    useEffect(() => {

        // Set up event listener for window resize
        const detectSize = () => {
            setWindowDimension({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', detectSize);

        // // Set timer for confetti
        // const timer = setTimeout(() => handleConfetti(), 8000);

        // Cleanup function
        return () => {
            window.removeEventListener('resize', detectSize);
            // clearTimeout(timer);
        };
    }, []); // Empty dependency array means this effect runs once on mount

    // Create unique house areas from projects
    const houseAreas = useMemo(() => {
        const uniqueAreas = new Map();

        // Add ALL option first
        uniqueAreas.set('all', {
            project_home_category_id: 'all',
            project_home_category: 'All Areas',
            house_category_icon: 'Home-Black.png' // You might want to adjust this icon
        });

        // Add other areas
        projects.forEach(project => {
            const id = project.project_home_category_id;
            if (!uniqueAreas.has(id)) {
                uniqueAreas.set(id, {
                    project_home_category_id: id,
                    project_home_category: project.project_home_category,
                    house_category_icon: project.house_category_icon
                });
            }
        });

        // Convert to array and sort alphabetically by category name, keeping 'All Areas' first
        return Array.from(uniqueAreas.values()).sort((a, b) => {
            if (a.project_home_category === 'All Areas') return -1;
            if (b.project_home_category === 'All Areas') return 1;
            return a.project_home_category.localeCompare(b.project_home_category);
        });
    }, [projects]);

    // Add state for selected filter
    const [selectedFilter, setSelectedFilter] = useState('all');

    // Filter projects based on selection
    const filteredProjects = useMemo(() => {
        if (selectedFilter === 'all') {
            return projects;
        }
        return projects.filter(project =>
            project.project_home_category_id === selectedFilter
        );
    }, [projects, selectedFilter]);

    // Get the address from property data and strip ", USA"
    const address = propertyData?.data?.property_details?.full_address.split(", USA")[0];

    // Create a handler for what happens when user adds a project --> We need to add the full project to the projectsToDiscuss array
    const handleProjectAdd = (projectId) => {
        const project = projects.find(project => project.project_id === projectId);
        setProjectsToDiscuss(prev => [...prev, project]);
    };

    // Create a handler for what happens when user removes a project --> We need to remove the project from projectsToDiscuss array based on project_id
    const handleProjectRemove = (projectId) => {
        setProjectsToDiscuss(prev => prev.filter(project => project.project_id !== projectId));
    };

    // Create a function for formatting the projectsToDiscuss array into a string
    const formatProjectsToDiscuss = () => {
        // Take the ids in projectsToDiscuss and use them to get the names of the projects from the projects array
        const projectNames = projectsToDiscuss.map(id => {
            const project = projects.find(project => project.project_id === id);
            return CleanProjectNames(project.project_name);
        });

        // Return the project names as a string
        return projectNames.join(', ');
    }


    // Static placeholder for name and email
    const name = "";
    const email = "";


    const renderStageZero = () => (
        <div className={`schedule-stage-container ${deviceType}`}>
            <p className="text-align-center" dangerouslySetInnerHTML={{ __html: intro_sentence }} />
            <CTAButton text="Select Projects" onClick={() => setProcessStage(1)} />
        </div>
    );


    const renderStageOne = () => (
        <div className={`schedule-stage-container ${deviceType}`}>
            {/* {showTitle &&
                <div className="schedule-stage-container-header">
                    <h2>Which projects are you looking for advice on?</h2>
                </div>
            } */}
            <div className={`review-all-projects__filter-section ${deviceType}`}>
                <span className='review-all-projects__filter-section-label'>Filter by:</span>
                <div className={`review-all-projects__filter-bar ${deviceType}`}>
                    {houseAreas.map(area => (
                        <button
                            key={area.project_home_category_id}
                            className={`review-all-projects__filter-button ${selectedFilter === area.project_home_category_id ? 'active' : ''}`}
                            onClick={() => setSelectedFilter(area.project_home_category_id)}
                        >
                            {area.project_home_category}
                        </button>
                    ))}
                </div>
            </div>
            <div className={`projects-grid send-schedule ${deviceType}`}>
                {filteredProjects.map(project => {
                    const isProjectSelected = projectsToDiscuss.some(p => p.project_id === project.project_id);
                    return (
                        <ProjectsListCard
                            key={project.project_id}
                            project={project}
                            handleProjectClick={isProjectSelected ? handleProjectRemove : handleProjectAdd}
                            isSelected={isProjectSelected}
                            showAllDetails={false}
                            simulatorView={true}
                            handleAddingProjectToSimulator={handleProjectAdd}
                            handleRemovingProjectFromSimulator={handleProjectRemove}
                        />
                    );
                })}
            </div>
            <div className={`back-button-container ${deviceType}`}>
                <CTAButton text="Back" onClick={() => setProcessStage(0)} category="back" />
                <CTAButton
                    text="Enter Recipient Emails"
                    onClick={projectsToDiscuss.length > 0 ? () => setProcessStage(2) : null}
                    category={projectsToDiscuss.length > 0 ? 'next' : 'disabled'}
                />
            </div>
            {/* Next
            </button> */}
        </div>
    );

    const renderStageTwo = () => (
        <div className={`schedule-stage-container ${deviceType}`}>
            <div className={`send-email-grid ${deviceType}`}>
                {/* Left Column */}
                <div className={`send-email-column send-email-column-left ${deviceType}`}>
                    <h2 className={`send-email-column-header left-column-header ${deviceType}`}>Email Details</h2>
                    <div className={`sender-details ${deviceType}`}>
                        <div className="detail-row">
                            <span className="send-email-detail-label">Your Name:</span>
                            <span className="send-email-detail-value">{name}</span>
                        </div>
                        <div className="detail-row">
                            <span className="send-email-detail-label">Your Email:</span>
                            <span className="send-email-detail-value">{email}</span>
                        </div>
                        <div className="detail-row">
                            <span className="send-email-detail-label">Project{(projectsToDiscuss.length === 1 ? '' : 's')} to Share:</span>
                        </div>
                        <div className="send-email-projects-grid">
                            {projectsToDiscuss.map(project => (
                                <ProjectsListCard
                                    key={project.project_id}
                                    project={project}
                                    // handleProjectClick={handleProjectClick}
                                    isSelected={projectsToDiscuss.some(p => p.project_id === project.project_id)}
                                    showAllDetails={false}
                                    simulatorView={false}
                                    handleAddingProjectToSimulator={handleProjectAdd}
                                    handleRemovingProjectFromSimulator={handleProjectRemove}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className={`send-email-column send-email-column-right ${deviceType}`}>
                    <h2 className={`send-email-column-header ${deviceType}`}>Who We're Sending This To</h2>
                    <div className={`send-email-form-group ${deviceType}`}>
                        <label>Recipient Email(s), Separated With a Comma *</label>
                        <input
                            type="text"
                            value={recipientEmails}
                            onChange={(e) => setRecipientEmails(e.target.value)}
                            className="send-email-form-input"
                        />
                    </div>
                    <div className="send-email-form-group">
                        <label>Personal Note / Any Details You'd Like to Add</label>
                        <textarea
                            value={personalNote}
                            onChange={(e) => setPersonalNote(e.target.value)}
                            className="send-email-form-textarea"
                            rows={4}
                        />
                    </div>
                    <CTAButton text="Send Email" onClick={() => setProcessStage(3)} />
                    {/* Send Email
                    </button> */}
                </div>
            </div>

            <div className="back-button-container">
                <CTAButton text="Back" onClick={() => setProcessStage(1)} category="back" />
                {/* Back
                </button> */}
            </div>
        </div>
    );

    const emailSentSentence = "Your email has been sent! Hopefully that sets up your conversation, but if you need anything else, just schedule time with us!";

    const renderStageThree = () => (
        <div className={`schedule-stage-container ${deviceType}`}>
            <p className="text-align-center" dangerouslySetInnerHTML={{ __html: emailSentSentence }} />
        </div>
    );

    const sectionHeader = "Share Projects You're Interested in With Those You Trust";
    // const intro_sentence = "Discussing potential improvements with family, friends, contractors, or whoever you trust is a great way to build comfort around a given project. Select projects below to send key information directly to anybody you want!"
    const intro_sentence = "Discussing potential improvements with family, friends, contractors, or whoever you trust is a great way to build comfort around a given project. We've made it easy to share projects that you are interested in directly with those you want to discuss the projects with!."

    const stagesArray = [
        {
            stage: 0,
            label: "About"
        },
        {
            stage: 1,
            label: "Select Projects"
        },
        {
            stage: 2,
            label: "Add Email Recipients"
        }
    ]

    useEffect(() => {
        if (processStage === 3) {
            handleConfetti();
        }
    }, [processStage]);


    return (
        <ContentWrapper inline={inline} handleOutsideClick={handleOutsideClick} confetti={showConfetti} deviceType={deviceType}>
            {showConfetti && (
                <Confetti
                    width={windowDimension.width}
                    height={windowDimension.height}
                    recycle={false}
                    numberOfPieces={400}
                />
            )}
            <div className="taking-action-section-container">
                <h1>{sectionHeader}</h1>
                <div className="send-project-to-others-stages-container">
                    {stagesArray.map((stage, index) => {

                        let stageStatus = "inactive";
                        if (index === processStage) {
                            stageStatus = "active";
                        } else if (index < processStage) {
                            stageStatus = "complete";
                        }

                        return (
                            <div className="send-project-to-others-stage" key={stage.stage}>
                                <div
                                    className={`send-project-to-others-stage-item ${stageStatus}`}
                                    onClick={index < processStage ? () => setProcessStage(index) : null}
                                >
                                    <span className="send-project-to-others-stage-label">{stage.label}</span>
                                </div>
                                {index < stagesArray.length - 1 && (
                                    <MoveRight className="send-project-to-others-stage-icon" />
                                )}
                            </div>
                        )
                    })}
                </div>
                {processStage === 0 && renderStageZero()}
                {processStage === 1 && renderStageOne()}
                {processStage === 2 && renderStageTwo()}
                {processStage === 3 && renderStageThree()}
            </div>
        </ContentWrapper>
    );
};

export default SendProjectToOthers;